import { useContext, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { LocationGroupScheduleComponentContext } from 'src/components/context';
import { useWindow } from 'src/hooks';
import { ILayout } from 'src/interfaces';
import { getScale } from 'src/utils';

import { getCurrentPositionGridLines } from '../../../ScheduleComponentPage.helper';
import { ALERT_BOX_HEIGHT } from '../../layoutScreenBuilder/LayoutScreenBuilder.styled';

export const useScheduleComponentLayout = () => {
  const [mockContainerRef, setMockContainerRef] = useState<{
    current: HTMLDivElement | null;
  }>({
    current: null,
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    layout,
    scheduleName,
    customerState,
    activeAssemblyJourney,
    isCodConfirmationOpen,
    showLayoutScreenBuilder,
    screenIndex,
    gridSectionsWithoutDisclaimer,
    formMethods,
    screenSections,
    showSectionDetailDrawer,
    currentSelectedSectionId,
    sectionIndex,
    layerScreenSections,
    layerIndex,
    positionsGridLines,
    previewingPositionIndex,
    schedule,
    isPreviewEnabled,
    isAdditionalLocationGroups,
    onActiveSectionChange,
    onGridSectionsChange,
    setLayout,
    onUpdateScreenPredefinedSections,
    onUpdatePositionsGridLines,
    handlePreviewSchedule,
  } = useContext(LocationGroupScheduleComponentContext);

  const { containerWidth, containerHeight } = useWindow(mockContainerRef);
  const isContainerReady = containerWidth > 0 && containerHeight > 0;

  const scale = useMemo(
    () =>
      isContainerReady
        ? getScale(containerWidth, containerHeight, layout?.screens ?? [])
        : 1,
    [containerHeight, containerWidth, isContainerReady, layout?.screens],
  );
  const scaleForSingleScreen = useMemo(
    () =>
      isContainerReady
        ? getScale(
            containerWidth,
            containerHeight - ALERT_BOX_HEIGHT,
            layout?.screens?.[0] ? [layout?.screens?.[0]] : [],
          )
        : 1,
    [containerHeight, containerWidth, isContainerReady, layout?.screens],
  );
  const canDeleteCurrentSelectedSection = useMemo(
    () =>
      !showSectionDetailDrawer ||
      currentSelectedSectionId !== screenSections?.[sectionIndex]?.i,
    [
      currentSelectedSectionId,
      screenSections,
      sectionIndex,
      showSectionDetailDrawer,
    ],
  );

  const currentPositionGridLines = getCurrentPositionGridLines(
    positionsGridLines,
    layout as ILayout,
    layerIndex,
    previewingPositionIndex,
  );

  useLayoutEffect(() => {
    if (containerRef?.current) {
      setMockContainerRef({
        current: containerRef?.current,
      });
    }
  }, [containerRef]);

  return {
    layout,
    scheduleName,
    containerRef,
    customerState,
    activeAssemblyJourney,
    isCodConfirmationOpen,
    showLayoutScreenBuilder,
    screenIndex,
    scale,
    scaleForSingleScreen,
    gridSectionsWithoutDisclaimer,
    formMethods,
    screenSections,
    canDeleteCurrentSelectedSection,
    layerScreenSections,
    layerIndex,
    positionsGridLines,
    currentPositionGridLines,
    schedule,
    isPreviewEnabled,
    showSectionDetailDrawer,
    isAdditionalLocationGroups,
    onActiveSectionChange,
    onGridSectionsChange,
    setLayout,
    onUpdateScreenPredefinedSections,
    onUpdatePositionsGridLines,
    handlePreviewSchedule,
  };
};
